import { Injectable } from '@angular/core';

import * as _ from 'lodash';

import { SocketService } from '../../../services/socket/socket.service';
import { LocalStorageManagerService } from '../../../../utilities/local-storage/local-storage-manager.service';
import { TeamnoteApiService } from '../../../../api/teamnote-api.service';
import { TeamNoteApiConstant } from '../../../../constants/api.constant';
import { MessageTypeConstant } from '../../../../constants/message-type.constant';
import { ChatMessageService } from '../../../services/data/messages/chat-message/chat-message.service';
import { LoggerService } from '../../../../utilities/logger/logger.service';
import { Message } from '../../../../models/message';
import { ChatPoll, ChatPollVoteUpdate } from '../../../../models/chat-poll';

/**
 * Handles chat poll (CHAT_POLL / CHAT_POLL_VOTE) read + vote traffic.
 *
 * A poll is created / voted on as an ordinary chat message (type 14 / 140),
 * there is no dedicated create / vote endpoint. This service only covers:
 * - reading poll results (/chat_poll/result), required because /load_history
 *   is cursor based (a cached poll is never re-fetched) and restricted polls
 *   send no live 140s to ordinary members
 * - ending a poll (/chat_poll/end)
 * - sending a vote (type 140), which is an ephemeral, non-persisted update and
 *   must never be inserted into the timeline as a local placeholder message
 * - applying incoming live poll updates (140) / API results onto the cached
 *   type-14 message, guarded by `updated_at` so a stale replay can never roll
 *   counts backwards
 */
@Injectable()
export class ChatPollService {

  // A live 140 carries counts but never voters, so a result fetch is the only way to refresh
  // them. Fetched immediately (so the avatars land with the counts) with at most one request
  // in flight per poll and one queued behind it, which is what keeps a burst of votes cheap.
  private _voterRefreshInFlight: { [messageId: string]: boolean } = {};
  private _voterRefreshPending: { [messageId: string]: boolean } = {};

  // Rendered poll messages, keyed by message_id. Needed because a message loaded through
  // /load_history is not put into ChatMessageService's cache, so a cache lookup alone would
  // silently drop poll updates for any poll the user scrolled back to.
  private _pollMessages: { [messageId: string]: Message } = {};

  constructor(
    private _socketService: SocketService,
    private _localStorageManagerService: LocalStorageManagerService,
    private _teamnoteApiService: TeamnoteApiService,
    private _chatMessageService: ChatMessageService,
    private _loggerService: LoggerService
  ) { }

  registerPollMessage(message: Message): void {
    if (message && message.message_id) {
      this._pollMessages[message.message_id] = message;
    }
  }

  unregisterPollMessage(messageId: string): void {
    delete this._pollMessages[messageId];
  }

  /**
   * Resolve the message object to apply poll state to: the rendered one if we know it,
   * otherwise whatever the chat message cache holds.
   */
  private getPollMessage(messageId: string): Message {
    return this._pollMessages[messageId] || this._chatMessageService.getChatMessageByMessageId(messageId);
  }

  /**
   * POST /chat_poll/result
   *
   * Must be called once when a poll card / detail view first renders, and again
   * after the user votes - this is required (not an optimisation), see class doc.
   */
  getPollResult(pollId: string, messageId: string, success: Function, failure?: Function): void {
    let params = this.getPollIdentifierParams(pollId, messageId);
    if (!params) {
      this._loggerService.warn('Trying to get poll result without poll id / message id, rejected');
      return;
    }
    let url = TeamNoteApiConstant.CHAT_POLL.RESULT;
    this._teamnoteApiService.callApi(
      url,
      params,
      (resp) => {
        success(resp);
      },
      (err) => {
        this._loggerService.error('Get poll result failed: ' + JSON.stringify(err));
        if (failure) {
          failure(err);
        }
      },
      false,
      false,
      true // isBackgroundTask, do not flash the global loading spinner
    );
  }

  /**
   * POST /chat_poll/end - creator only.
   * A 200 with { success: false, error_msg: "Poll Already Ended" } is not an error, callers
   * should check `resp.success` themselves.
   */
  endPoll(pollId: string, messageId: string, success: Function, failure?: Function): void {
    let params = this.getPollIdentifierParams(pollId, messageId);
    if (!params) {
      this._loggerService.warn('Trying to end poll without poll id / message id, rejected');
      return;
    }
    let url = TeamNoteApiConstant.CHAT_POLL.END;
    this._teamnoteApiService.callApi(
      url,
      params,
      (resp) => {
        success(resp);
      },
      (err) => {
        this._loggerService.error('End poll failed: ' + JSON.stringify(err));
        if (failure) {
          failure(err);
        }
      },
      false,
      false,
      true
    );
  }

  /**
   * Send a vote / change vote / retract vote (type 140).
   *
   * A vote REPLACES the member's whole selection, [] is a valid retract.
   * This is an ephemeral state update (not a persisted message), so it is sent
   * directly through the socket - no local placeholder message is inserted,
   * mirroring MessagesService.sendMessageStar(). Never set `received_by` here.
   */
  sendVote(chatId: string, pollId: string, messageId: string, optionIds: string[], callback?: Function): void {
    if (!chatId || !pollId || !messageId) {
      this._loggerService.warn('Trying to send poll vote without chat id / poll id / message id, rejected');
      return;
    }
    let correlationId = 'CHAT_POLL_VOTE_' + chatId + '_' + _.now();
    let routingKey = '/' + chatId;
    let headers = {
      'correlation-id': correlationId,
      'device_token': this._localStorageManagerService.getDeviceToken(),
      'type': MessageTypeConstant.CHAT_POLL_VOTE,
      'priority': 1
    };
    let body = JSON.stringify({
      poll_id: pollId,
      message_id: messageId,
      option_ids: optionIds || []
    });

    this._socketService.sendMessage(routingKey, headers, body, correlationId, callback);
  }

  /**
   * Apply an incoming type-140 (ephemeral vote update) onto the cached type-14 poll message.
   */
  applyPollVoteUpdate(m: Message): void {
    let update: ChatPollVoteUpdate = JSON.parse(m.body);
    if (!update || !update.message_id) {
      this._loggerService.warn('Received poll vote update without target message_id, rejected');
      return;
    }

    let targetMessage = this.getPollMessage(update.message_id);
    if (!targetMessage || !targetMessage.poll) {
      return;
    }

    if (!this.isNewerUpdate(targetMessage.poll.updated_at, update.updated_at)) {
      // stale replay (e.g. offline device replaying 140s on reconnect), discard
      return;
    }

    _.each(targetMessage.poll.options, (option) => {
      if (update.option_counts && update.option_counts.hasOwnProperty(option.option_id)) {
        option.vote_count = update.option_counts[option.option_id];
      }
    });
    if (!_.isUndefined(update.total_voters)) {
      targetMessage.poll.total_voters = update.total_voters;
    }
    targetMessage.poll.is_ended = update.is_ended;
    targetMessage.poll.vote_end = update.vote_end;
    targetMessage.poll.updated_at = update.updated_at;

    this._chatMessageService.updateChatMessageSubject();
    this._chatMessageService.updateActiveChatRoomMessageSubject();

    // The 140 has no `voters`, so without this the per-option avatars would keep showing
    // whoever had voted the last time a result was fetched.
    this.scheduleVoterRefresh(targetMessage);
  }

  /**
   * Pull a fresh /chat_poll/result for a poll that just changed, to pick up the voter list.
   *
   * Deliberately not delayed: the counts have already been applied from the 140, so any wait
   * here shows up as avatars trailing the numbers. Instead, only one request per poll is ever
   * in flight and further updates arriving meanwhile collapse into a single follow-up.
   */
  private scheduleVoterRefresh(message: Message): void {
    let messageId = message.message_id;
    if (!messageId) {
      return;
    }

    if (this._voterRefreshInFlight[messageId]) {
      this._voterRefreshPending[messageId] = true;
      return;
    }
    this._voterRefreshInFlight[messageId] = true;

    let onSettled = () => {
      delete this._voterRefreshInFlight[messageId];
      if (this._voterRefreshPending[messageId]) {
        delete this._voterRefreshPending[messageId];
        this.scheduleVoterRefresh(message);
      }
    };

    this.getPollResult(
      message.poll ? message.poll.poll_id : null,
      messageId,
      (resp: ChatPoll) => {
        this.mergePollResult(messageId, resp);
        onSettled();
      },
      () => onSettled()
    );
  }

  /**
   * Apply a /chat_poll/result payload onto a cached message's poll, guarded by the same
   * `updated_at` rule so a stale queued 140 applied afterwards cannot roll it back.
   */
  mergePollResult(messageId: string, result: ChatPoll): void {
    if (!messageId || !result) {
      return;
    }
    let targetMessage = this.getPollMessage(messageId);
    if (!targetMessage) {
      return;
    }

    // Unlike a live 140 (which must be strictly newer to apply), a freshly fetched API result is
    // always current data - it should be applied even when updated_at is unchanged (e.g. no votes
    // since last read), since it is also the only place `voters` is populated. Only reject it if it
    // is somehow OLDER than what we already hold (e.g. an out-of-order response).
    if (targetMessage.poll && !_.isUndefined(targetMessage.poll.updated_at) && !_.isNull(targetMessage.poll.updated_at)) {
      if (result.updated_at < targetMessage.poll.updated_at) {
        return;
      }
    }

    if (targetMessage.poll) {
      // Update in place rather than swapping the reference: a rendered poll card holds a
      // reference to this object, and replacing it leaves the live card showing stale state
      // (e.g. still offering to vote on a poll that has just ended).
      _.assign(targetMessage.poll, result);
    } else {
      targetMessage.poll = result;
    }

    this._chatMessageService.updateChatMessageSubject();
    this._chatMessageService.updateActiveChatRoomMessageSubject();
  }

  /**
   * Both read APIs identify a poll by EITHER poll_id or message_id. A poll delivered live carries
   * no `poll` state (the mailbox dict is built before the poll rows are persisted), so a freshly
   * created poll is only addressable by its message_id until the first result comes back.
   */
  private getPollIdentifierParams(pollId: string, messageId: string): any {
    if (pollId) {
      return { poll_id: pollId };
    }
    if (messageId) {
      return { message_id: messageId };
    }
    return null;
  }

  private isNewerUpdate(currentUpdatedAt: number, incomingUpdatedAt: number): boolean {
    if (_.isUndefined(currentUpdatedAt) || _.isNull(currentUpdatedAt)) {
      return true;
    }
    return incomingUpdatedAt > currentUpdatedAt;
  }

}
